/* eslint-disable no-await-in-loop */
/* eslint-disable class-methods-use-this */
// eslint-disable-next-line import/no-cycle
import { apiGet, apiPost } from '@/store/api';
import { useApplicationStore } from '@/store/applicationStore';

interface IRecipient {
  NAME: string;
  EMAIL: string;
}

interface IDraftEmailFromTemplate {
  mailNo: number;
  emailId: number;
  subject: string;
  content: string;
  to: IRecipient[];
  cc: IRecipient[];
  bcc: IRecipient[];
  createdBy: string;
  location?: string;
  sednaCategoryId: number;
}

interface ISednaDraftEmail {
  data: {
    type: string;
    attributes: {
      fromUser: string;
      fromTeam: string;
      body: string;
      subject: string;
      to: { email: string; }[];
      cc: { email: string; }[];
      bcc: { email: string; }[];
    };
  };
}

interface ISednaDraftEmailResponse {
  data: {
    id: string;
  };
  errors?: {
    code?: string;
    detail?: string;
    status?: number;
  }[];
  warnings: string[];
}

interface ISednaNewEmailResponse {
  url?: string;
  error?: string;
  warning?: string;
}

interface ISednaEmailResponseData {
  id: string;
  attributes: {
    bcc?: string[];
    cc?: string[];
    from?: string[];
    hasAttachment: boolean;
    messageId?: string;
    receivedAt?: string;
    sentAt?: string;
    subject?: string;
    to?: string[];
  };
  relationships: {
    categoryTag: {
      data:
      {
        id: string | null;
        label: string | null;
        type: string | null;
      }[];
    };
    document: {
      data: {
        id: string | null;
        type: string | null;
      }[];
    };
  }
}

interface ISednaEmailResponseBody {
  data: ISednaEmailResponseData[];
}

interface ISednaCategoryTag {
  id?: string;
  type?: string;
  attributes?: {
    label?: string;
    name?: string;
    archived?: boolean;
  };
}

export default class SednaApiService {
  applicationStore = useApplicationStore();

  isSednaIntegration = false;

  agencySetting;

  agencyId;

  private categoryTagsCache: ISednaCategoryTag[] | null = null;

  private categoryTagsCacheTimestamp: number | null = null;

  // Cache expiration time in milliseconds (default: 2 hours)
  private readonly CACHE_EXPIRATION_TIME = 2 * 3600000;

  constructor(agencyId: number) {
    this.agencyId = agencyId;
    this.agencySetting = this.applicationStore.agencySettings.find((agency) => agency.ID === agencyId);
    this.isSednaIntegration = (this.agencySetting?.EMAIL_TYPE ?? 0) === 7;
  }

  private isCacheValid(): boolean {
    if (!this.categoryTagsCache || !this.categoryTagsCacheTimestamp) {
      return false;
    }

    const now = Date.now();
    return (now - this.categoryTagsCacheTimestamp) < this.CACHE_EXPIRATION_TIME;
  }

  public clearCategoryTagsCache(): void {
    this.categoryTagsCache = null;
    this.categoryTagsCacheTimestamp = null;
  }

  private async getCategoryTags(forceRefresh: boolean = false): Promise<ISednaCategoryTag[]> {
    if (!forceRefresh && this.isCacheValid() && this.categoryTagsCache) {
      return this.categoryTagsCache;
    }

    const categoryTags: ISednaCategoryTag[] = await apiGet(`sedna/all-category-tags/${this.agencyId}`);

    this.categoryTagsCache = categoryTags;
    this.categoryTagsCacheTimestamp = Date.now();

    return categoryTags;
  }

  public async refreshCategoryTagsCache(): Promise<ISednaCategoryTag[]> {
    return this.getCategoryTags(true);
  }

  public async getAllCategoryTags(): Promise<ISednaCategoryTag[]> {
    return this.getCategoryTags();
  }

  fileToBase64(file: File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async getAllSednaEmail(sednaJobId: string, onlyForCount: boolean = false): Promise<ISednaEmailResponseBody[]> {
    // onlyForCount: Hent mail for å vise/skjule øye ikon i email tab
    const { SEDNA_MAIL_FETCH_LIMIT } = this.agencySetting;
    if (SEDNA_MAIL_FETCH_LIMIT === 0) {
      return [];
    }

    const getMessagesResponses: ISednaEmailResponseBody[] = await apiPost(`sedna/mail/fetch-all/${this.agencyId}/${onlyForCount}`, {
      SEDNA_JOB_ID: sednaJobId,
    });

    if (onlyForCount) {
      return getMessagesResponses;
    }

    const categoryTags = await this.getCategoryTags();
    getMessagesResponses.forEach(response => {
      response.data.forEach((eml: ISednaEmailResponseData) => {
        eml.relationships?.categoryTag?.data.forEach((tag) => {
          // eslint-disable-next-line no-param-reassign
          tag.label = categoryTags.find((cat: ISednaCategoryTag) => cat.id === tag.id)?.attributes?.label || null;
        })
      })
    })

    return getMessagesResponses;
  }

  async getSednaJobId(locationId: number, location: string) {
    const sednaJobId: string | null = await apiGet(`sedna/sednajobid/${location}/${locationId}`);
    return sednaJobId;
  }

  createComposeEditSednaEmailUrl(sednaMailId: string) {
    const { SEDNA_URL, SEDNA_TEAM_ID } = this.agencySetting;
    const urlParts = SEDNA_URL.split('.com');
    const teamPart = SEDNA_TEAM_ID ? `?teamId=${SEDNA_TEAM_ID}` : '';
    const externalSednaUrl = `${urlParts[0]}.com/compose/message/${sednaMailId ?? 'new'}${teamPart}`;
    return externalSednaUrl;
  }

  createOpenSednaEmailUrl(sednaMailId: number) {
    const { SEDNA_URL, SEDNA_TEAM_ID } = this.agencySetting;
    const urlParts = SEDNA_URL.split('.com');
    const teamPart = SEDNA_TEAM_ID ? `?teamId=${SEDNA_TEAM_ID}` : '';
    const externalSednaUrl = `${urlParts[0]}.com/list/message/${sednaMailId}${teamPart}`;
    return externalSednaUrl;
  }

  createReplySednaEmailUrl(sednaMailId: number, replyType: null | 'all' | 'forward' | 'open') {
    if (replyType === 'open') {
      return this.createOpenSednaEmailUrl(sednaMailId);
    }
    const { SEDNA_URL, SEDNA_TEAM_ID } = this.agencySetting;
    const urlParts = SEDNA_URL.split('.com');
    const teamPart = SEDNA_TEAM_ID ? `?teamId=${SEDNA_TEAM_ID}` : '';
    const firstPart = `${urlParts[0]}.com/follow-up/message/${sednaMailId}/`;
    // eslint-disable-next-line no-nested-ternary
    const middlePart = replyType === 'all' ? 'replyAll' : replyType === 'forward' ? 'forward' : 'reply';
    const externalSednaUrl = `${firstPart}${middlePart}${teamPart}`;
    return externalSednaUrl;
  }

  async openSednaUrlForJobTag(
    sednaJobId: string,
    allTeams: boolean,
    mailboxType: 'all' | 'draft' | 'inbox' | 'myWork' | 'following' | 'sent' = 'all'
  ) {
    const { SEDNA_URL, SEDNA_TEAM_ID } = this.agencySetting;
    const urlParts = SEDNA_URL.split('.com');

    const mailBoxToFilter = `&filter=${mailboxType}`;
    const teamFilter = SEDNA_TEAM_ID ? `&teamId=${SEDNA_TEAM_ID}` : '';
    const teamsFilter = allTeams ? '&teams=all' : '';
    const responseJson = await apiPost(`sedna/sednajobref/details/${this.agencyId}`, {
      SEDNA_JOB_ID: sednaJobId,
    });
    if (responseJson?.data?.attributes) {
      const urlParams = {
        type: 'and',
        children: [
          {
            term: {
              type: 'jobReference',
              query: responseJson.data.attributes.name,
              _data: { name: responseJson.data.attributes.name, tagId: responseJson.data.attributes.tagId },
            },
          },
        ],
      };
      const firstPart = `${urlParts[0]}.com/list?filterSet=`;
      const middlePart = encodeURI(JSON.stringify(urlParams));
      const lastPart = `${mailBoxToFilter}${teamFilter}${teamsFilter}`;
      const externalSednaUrl = `${firstPart}${middlePart}${lastPart}`;
      return externalSednaUrl;
    }
    return null;
  }

  async createSednaJobRef(locationId: number, location: string) {
    // create job reference and job id in gs-backend
    const sednaPostResponseJson = await apiGet(`sedna/sednajobref/create/${location}/${locationId}/${this.agencyId}`);

    return sednaPostResponseJson?.payload; // sedna job id
  }

  async updateSednaJobIdInSedna(location: string, locationId: number, sednaJobId: string, followers: string[]) {
    const success = await apiPost(`sedna/sednajobref/update/${location}/${locationId}/${this.agencyId}`, {
      SEDNA_JOB_ID: sednaJobId,
      HAS_FOLLOWERS: followers.length > 0,
    });

    return success;
  }

  splitMultipleReceipients(email: string, name: string) {
    const emails = email.replace(/\s/g, '').split(/[,;]/g);
    if (emails.length > 1) {
      return emails.map((eml) => ({ email: eml }));
    }
    return emails.map((eml) => ({ email: eml, name }));
  }

  openNewSednaEmailUrl(sednaResponse: ISednaNewEmailResponse) {
    if (sednaResponse.warning) {
      window.messageBus.$emit('showMessage', {
        type: 'warning',
        text: 'Some issues need your attention',
        subText: `${sednaResponse.warning}` || '',
        icon: true,
      });
    }

    if (sednaResponse.error) {
      window.messageBus.$emit('showMessage', {
        type: 'error',
        text: 'Error sending sedna email',
        subText: `${sednaResponse.error}` || '',
        icon: true,
      });
    }

    if (sednaResponse.url) {
      window.open(sednaResponse.url, '_blank');
    }
  }

  async composeNewSednaEmail(sednaJobId: string, attachments: File[], draftMessage?: IDraftEmailFromTemplate): Promise<ISednaNewEmailResponse> {
    const { SEDNA_TEAM_ID } = this.agencySetting;
    const fromUser = this.applicationStore.user.sednaUserId?.length
      ? this.applicationStore.user.sednaUserId
      : this.applicationStore.user.userRights['U.EMAIL'];
    const fromTeam = SEDNA_TEAM_ID?.length ? SEDNA_TEAM_ID : '';
    const categoryId = draftMessage?.sednaCategoryId ?? null;

    const sednaDraftMessage: ISednaDraftEmail = {
      data: {
        type: 'string',
        attributes: {
          fromUser,
          fromTeam,
          body: draftMessage?.content ?? '',
          subject: draftMessage?.subject ?? '',
          to: draftMessage?.to?.map((t) => this.splitMultipleReceipients(t.EMAIL, t.NAME))?.flat() ?? [],
          cc: draftMessage?.cc?.map((t) => this.splitMultipleReceipients(t.EMAIL, t.NAME))?.flat() ?? [],
          bcc: draftMessage?.bcc?.map((t) => this.splitMultipleReceipients(t.EMAIL, t.NAME))?.flat() ?? [],
        },
      },
    };

    // create attachments list
    const base64Attachments = attachments.length > 0 ? await Promise.all(attachments.map(async (file) => {
      const fileAsBase64 = await this.fileToBase64(file);
      const attachment = { data: { base64Content: fileAsBase64, fileName: file.name, fileSize: file.size } };
      return attachment;
    })) : [];

    // create a draft message
    const responseJson: ISednaDraftEmailResponse = await apiPost(`sedna/mail/new/${this.agencyId}`, {
      SEDNA_JOB_ID: sednaJobId,
      CATEGORY_TAG_ID: categoryId,
      ATTACHMENTS: base64Attachments,
      DRAFT_MESSAGE: JSON.stringify(sednaDraftMessage),
    });

    // open sedna web editor or gatship web email editor
    const sednaMailId = responseJson?.data?.id;
    const externalSednaUrl = sednaMailId && this.createComposeEditSednaEmailUrl(sednaMailId);

    // eslint-disable-next-line consistent-return
    return {
      url: externalSednaUrl,
      warning: responseJson.warnings.join('\r\n'),
      error: responseJson?.errors?.map((err) => `${err?.code} (${err?.status}): ${err?.detail}`).join('\r\n'),
    };
  }
}
